<template>
  <div class="terms-view">
    <!-- Hero Section -->
    <section class="hero-section">
      <div class="container">
        <div class="hero-content">
          <h1 class="hero-title">
            Music Distribution Agreement
          </h1>
          <p class="hero-subtitle">
            Between Artist and Indiefy Inc.
          </p>
          <div class="hero-badge">
            <i class="fas fa-shield-alt mr-2" />
            Terms & Conditions
          </div>
        </div>
      </div>
    </section>

    <!-- Table of Contents -->
    <section class="toc-section">
      <div class="container">
        <div class="toc-card">
          <h2 class="toc-title">
            Table of Contents
          </h2>
          <div class="toc-grid">
            <a
              v-for="(section, index) in tableOfContents"
              :key="index"
              :href="`#section-${index + 1}`"
              class="toc-item"
              @click="scrollToSection(index + 1)"
            >
              <span class="toc-number">{{ index + 1 }}</span>
              <span class="toc-text">{{ section }}</span>
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- Terms Content -->
    <section class="terms-content">
      <div class="container">
        <div class="content-wrapper">
          <!-- Section 1: Definitions -->
          <div
            id="section-1"
            class="terms-section"
          >
            <h2 class="section-title">
              1. Definitions
            </h2>
            <div class="definition-list">
              <div class="definition-item">
                <h4>1.1 Publisher:</h4>
                <p>Represents the entity that publishes the content provided by the Artist directly into the DSP's with the Artist consent and approval.</p>
              </div>
              <div class="definition-item">
                <h4>1.2 Artist:</h4>
                <p>Represents the individual that creates, uploads and manages all intellectual material provided to the Publisher.</p>
              </div>
              <div class="definition-item">
                <h4>1.3 DSP:</h4>
                <p>(Digital Service Providers): Entities where the content and metadata is delivered for user consumption and monetization (digital music stores).</p>
              </div>
              <div class="definition-item">
                <h4>1.4 Metadata:</h4>
                <p>Refers to all the data registered into the content, provided by the Artist itself.</p>
              </div>
              <div class="definition-item">
                <h4>1.5 Copyright holder:</h4>
                <p>Refers to the individual (Artist) or party (Label) that owns the master of the recordings.</p>
              </div>
            </div>
          </div>

          <!-- Section 2: License Terms -->
          <div
            id="section-2"
            class="terms-section"
          >
            <h2 class="section-title">
              2. License Terms
            </h2>
            <div class="terms-content-text">
              <p>AGREEMENT made and entered into this herein below date, By and between Indiefy Inc.</p>
              <p>(hereinafter referred to as the "Publisher") and [Artist Legal Name] (hereinafter referred to as the "Artist").</p>
              <p>The Publisher and Artist agrees as follows concerning the musical composition(s) uploaded to the platform https://indiefy.net now referred as the "Recordings" under the Artist's stage name: [Artist]</p>
              <p>Upon the timely occurrence and performance of all material events and obligations required to produce the Album or Single, Artist shall grant to the Publisher the exclusive license to use the name [Artist], and the Artist's photographic image, in the promotion and distribution of the Release.</p>
              <p>For good and valuable consideration by each of the parties hereto given to the other, receipt of which is hereby acknowledged, and in consideration of the promises and covenants hereinafter contained, IT IS AGREED AS FOLLOWS:</p>
            </div>
          </div>

          <!-- Section 3: Distribution Process -->
          <div
            id="section-3"
            class="terms-section"
          >
            <h2 class="section-title">
              3. Distribution Process and Metadata
            </h2>
            <div class="terms-content-text">
              <p><strong>1.</strong> Distribution process will take between 10-15 days solely depending on DSP's ingestion time.</p>
              <p><strong>2.</strong> Publisher will let the Artist know about the distribution status using the following status labels:</p>

              <div class="status-list">
                <div class="status-item in-review">
                  <div class="status-badge">
                    In-review
                  </div>
                  <p>Release has been received by Publisher staff and is being currently reviewed and scanned.</p>
                </div>
                <div class="status-item in-process">
                  <div class="status-badge">
                    In-process
                  </div>
                  <p>Release has been accepted and it has been sent to the correspondent DSP's. From here Publisher will depend on any DSP ingestion time, Publisher will not take any responsibilities if release suffers any delay. Publisher will follow with supply chain to resolve the delay.</p>
                </div>
                <div class="status-item published">
                  <div class="status-badge">
                    Published
                  </div>
                  <p>Release has been published in all/some DSP's. Some DSP's may publish the release after the release date chosen by the artist. Publisher will not take any responsibilities if release suffers any delay. Publisher will follow with supply chain to resolve the delay.</p>
                </div>
                <div class="status-item reopen">
                  <div class="status-badge">
                    Reopen
                  </div>
                  <p>If release is found with any problem by the Distribution department, it will be reopened. Reasons will be delivered to the artist via email or inside the platform section "My Music". Delivery time will be reset if reopen status is applied to the respective release, meaning it could NOT be delivered as of the original chosen release date by the Artist.</p>
                </div>
                <div class="status-item rejected">
                  <div class="status-badge">
                    Rejected
                  </div>
                  <p>If release is found with any problem by the distribution department, remarking copyright, plagiarism or low quality it will be rejected. Meaning reopen status will NOT be issued. Artist will receive one (1) strike for each rejected submission. If the Artist receives a total of two (2) strikes, Publisher will limit the Artist account until the problem is clear.</p>
                </div>
                <div class="status-item strike">
                  <div class="status-badge">
                    Strike
                  </div>
                  <p>The Artist can get these strikes when the distribution team finds several copyright issues (stolen song) the user is a fake artist, the user uses famous artists as featuring artist only to take advantage of their popularity on stores.</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Section 4: Artist Responsibilities -->
          <div
            id="section-4"
            class="terms-section"
          >
            <h2 class="section-title">
              4. Artist Responsibilities
            </h2>
            <div class="terms-content-text">
              <p><strong>1.</strong> The Artist warrant, represent and undertake to us that:</p>

              <div class="responsibility-list">
                <div class="responsibility-item">
                  <span class="responsibility-letter">(a)</span>
                  <p>The artist declares that he is of legal age (in the laws of his country) and that in case of being 17 years old, a verification of his account will be necessary, as well as the signed permission with identification of his tutor.</p>
                </div>
                <div class="responsibility-item">
                  <span class="responsibility-letter">(b)</span>
                  <p>The Recordings mentioned on this Agreement are the Artist original work and the Artist own the entire right, title and interest in and to the recordings and/or have all necessary licenses, rights, consents and permissions to use and exploit, and to authorize us to use and exploit the Recordings (including without limitation, all necessary rights in respect of the underlying musical compositions embodied in the Recordings) in the manner contemplated by these Terms.</p>
                </div>
                <div class="responsibility-item">
                  <span class="responsibility-letter">(c)</span>
                  <p>The Recordings (in whole or in part) does not defame third parties or infringe upon the copyright or other intellectual property rights, or rights of privacy of any third parties.</p>
                </div>
                <div class="responsibility-item">
                  <span class="responsibility-letter">(d)</span>
                  <p>The Artist is solely responsible for any claim or legal action, judicial or extrajudicial, initiated by third parties against the person responsible based on the use of the Recordings.</p>
                </div>
                <div class="responsibility-item">
                  <span class="responsibility-letter">(e)</span>
                  <p>The Artist is solely responsible for ANY material that he distributes through Indiefy.net and for the actions taken by third parties in which he is involved, whether legally or by any other means.</p>
                </div>
                <div class="responsibility-item">
                  <span class="responsibility-letter">(f)</span>
                  <p>Copyright claims will be subject to action as mentioned on Section 7 LEGAL ACTIONS.</p>
                </div>
              </div>

              <div class="additional-responsibilities">
                <p><strong>2.</strong> The Copyright shall be registered in the name of the Artist.</p>
                <p><strong>3.</strong> Artist is responsible to review all release information metadata and audio samples before sending to DSP's. Publisher will not take any responsibility for wrong metadata being submitted, this include: cover art, artist name that not match with his artist profile on music stores, copyholder, genre, audio file etc.</p>
                <p><strong>4.</strong> The artist must wait 6 months after the release approval to request a takedown. Takedown time will solely depend on the DSP after solicited by Publisher.</p>
                <p><strong>5.</strong> Artists will be solely responsible for any bank transfer and third party transaction fees or other charges related to such withdrawals.</p>
              </div>
            </div>
          </div>

          <!-- Section 5: Revenue/Payment -->
          <div
            id="section-5"
            class="terms-section"
          >
            <h2 class="section-title">
              5. Revenue / Payment
            </h2>
            <div class="terms-content-text">
              <div class="revenue-highlight">
                <div class="revenue-percentage">
                  <span class="percentage-number">85%</span>
                  <span class="percentage-text">Net Royalties</span>
                </div>
                <p>Indiefy will pay Artist eighty five percent (85%) of any Net Royalties received from the authorized Third Party Music Services.</p>
              </div>

              <div class="deductions-list">
                <h4>"Net Royalties" as used herein means Indiefy's actual receipts from the Third Party Music Services for the sale, distribution or other use of your Catalog, deducting any:</h4>
                <ul>
                  <li>(a) Tax, bank transfer and PayPal or other payment services transaction fees (if any);</li>
                  <li>(b) Marketplace costs, including without limitation any fees, payments, royalties or other consideration payable by Indiefy to song aggregators and digital retail stores;</li>
                  <li>(c) Licensing fees and royalties payable to song publishing licensors and compulsory mechanical licensors, as applicable.</li>
                </ul>
              </div>

              <div class="payment-terms">
                <p>Indiefy shall be solely responsible for tracking and paying any taxes and governmental fees associated with or incurred in exploitation of the Catalog through the Service, including without limitation any tariffs, value-added taxes or governmental fees.</p>
                <p>Accounting statements and your Net Royalties will be posted to your Indiefy user account in a timely fashion after Indiefy's receipt thereof. The frequency of sales reports as well as the amount and frequency of payouts totally depends on which stores the music was published in and their cut dates. Furthermore, the region/territory in which it was released and the amount of total streams is a crucial base for the statements. Once payment has been credited to your Indiefy user account, you will be able to withdraw the outstanding amount at your discretion.</p>

                <div class="minimum-payout">
                  <div class="payout-tier">
                    <span class="tier-label">Free Users:</span>
                    <span class="tier-amount">$100 USD</span>
                  </div>
                  <div class="payout-tier">
                    <span class="tier-label">PRO Users:</span>
                    <span class="tier-amount">$50 USD</span>
                  </div>
                </div>

                <p>Unless other payment arrangements are made between you and Indiefy, Indiefy shall remit payment to you according to the settings in your Indiefy Dashboard.</p>
                <p><strong>(d)</strong> The user that the artist used to create his account and in which the royalties have been reported must be active at ALL times in order to be able to make the payment of his royalties, once his user is deleted Indiefy is not responsible for reporting any other report received late from your artist or process your payment request if at the time of doing so your user is disabled by the artist.</p>
                <p><strong>(e)</strong> The payment period could be between 30 to 90 days after the payout request from the user account.</p>
              </div>
            </div>
          </div>

          <!-- Section 6: Publisher Responsibilities -->
          <div
            id="section-6"
            class="terms-section"
          >
            <h2 class="section-title">
              6. Publisher Responsibilities & Rights
            </h2>
            <div class="terms-content-text">
              <p>Publisher shall pay to Artist EIGHTY FIVE PERCENT (85%) of net publishing receipts less the deductions taken by the distribution providers.</p>

              <div class="publisher-duties">
                <div class="duty-item">
                  <span class="duty-number">1.</span>
                  <p>Publisher shall render sales statements to the Artist forty five days after every quarter of the year. (This period (45 days) may vary, it will solely depend on the DSP).</p>
                </div>
                <div class="duty-item">
                  <span class="duty-number">2.</span>
                  <p>a) For FREE users: Artist may only request a payment if the statement balance is equal to or greater than $100 USD. b) For PRO users: Artist may only request a payment if the statement balance is equal to or greater than $50 USD per registered artist, amounts between artists are not cumulative.</p>
                </div>
                <div class="duty-item">
                  <span class="duty-number">3.</span>
                  <p>Only the Publisher shall issue licenses and sub-publication rights.</p>
                </div>
                <div class="duty-item">
                  <span class="duty-number">4.</span>
                  <p>Publisher will provide the correspondent ISRC and UPC codes to the Artist when requested by the Artist.</p>
                </div>
                <div class="duty-item">
                  <span class="duty-number">5.</span>
                  <p>Publisher shall deduct from gross publishing receipts (monies received) for the following expenses to the extent that said expenses were actually paid or incurred by the Publisher including, but not limited to, songwriter's royalties and advances, registration fees, registration codes, store ingestion, demonstration tapes, lead sheets, arrangements, and all other miscellaneous, fees and expenses incurred in behalf of said composition.</p>
                </div>
                <div class="duty-item">
                  <span class="duty-number">6.</span>
                  <p>Publisher shall be free to make licensing and sub-publication agreements in benefit of the Artist without consulting Artist and upon whatever terms it deems wise or wishes.</p>
                </div>
                <div class="duty-item">
                  <span class="duty-number">7.</span>
                  <p>The Publisher will NOT be responsible for ANY material uploaded by the Artist. The Publisher will use available technology to scan for potential fraud, copyrighted material or similarities on the beat with other published songs.</p>
                </div>
                <div class="duty-item">
                  <span class="duty-number">8.</span>
                  <p>Publisher reserves the right to amend, discontinue or terminate our Services under this Agreement, at any time and without notification to the artist. Publisher reserves the right to reject or remove any Recordings or Materials from the Stores and our Services, that you have uploaded through the Services. We also reserve the right to terminate your access to the Stores or Services without notice.</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Section 7: Legal Actions -->
          <div
            id="section-7"
            class="terms-section"
          >
            <h2 class="section-title">
              7. Legal Actions
            </h2>
            <div class="terms-content-text">
              <div class="legal-warning">
                <i class="fas fa-exclamation-triangle" />
                <p><strong>1.</strong> Copyright claims issued by third parties, stores or personas will be subject to a full investigation and if is requested by the government of any country we will help them to take legal action against the Artist and/or the Legal Name individual.</p>
              </div>

              <p><strong>2.</strong> Following actions will be immediately applied to the Artist after Copyright claim is received and investigated:</p>

              <div class="action-list">
                <div class="action-item">
                  <span class="action-letter">a)</span>
                  <p>If Artist is found culpable of his/her actions, Immediate takedown of the Artist's content will be applied without notice, Artist may be contacted after the action taken via email.</p>
                </div>
                <div class="action-item">
                  <span class="action-letter">b)</span>
                  <p>If Artist generated royalties with content not being of his own property (Copyrighted content) use bots to get plays, or any other suspicious activity. Artist will NOT be entitled to reported or receive any generated royalties and his music will be takedown from our platform.</p>
                </div>
                <div class="action-item">
                  <span class="action-letter">c)</span>
                  <p>Any misuse that is made of our brand, logo and social networks will have a legal consequence to whom it is responsible, as well as the actions that derive from this will be the total responsibility of the person who acts in bad faith when making use of our brand.</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Section 6: Publisher Responsibilities -->
          <div
            id="section-6"
            class="terms-section"
          >
            <h2 class="section-title">
              6. Publisher Responsibilities & Rights
            </h2>
            <div class="terms-content-text">
              <p>Publisher shall pay to Artist EIGHTY FIVE PERCENT (85%) of net publishing receipts less the deductions taken by the distribution providers.</p>

              <div class="publisher-duties">
                <div class="duty-item">
                  <span class="duty-number">1.</span>
                  <p>Publisher shall render sales statements to the Artist forty five days after every quarter of the year. (This period (45 days) may vary, it will solely depend on the DSP).</p>
                </div>
                <div class="duty-item">
                  <span class="duty-number">2.</span>
                  <p>a) For FREE users: Artist may only request a payment if the statement balance is equal to or greater than $100 USD. b) For PRO users: Artist may only request a payment if the statement balance is equal to or greater than $50 USD per registered artist, amounts between artists are not cumulative.</p>
                </div>
                <div class="duty-item">
                  <span class="duty-number">3.</span>
                  <p>Only the Publisher shall issue licenses and sub-publication rights.</p>
                </div>
                <div class="duty-item">
                  <span class="duty-number">4.</span>
                  <p>Publisher will provide the correspondent ISRC and UPC codes to the Artist when requested by the Artist.</p>
                </div>
                <div class="duty-item">
                  <span class="duty-number">5.</span>
                  <p>Publisher shall deduct from gross publishing receipts (monies received) for the following expenses to the extent that said expenses were actually paid or incurred by the Publisher including, but not limited to, songwriter's royalties and advances, registration fees, registration codes, store ingestion, demonstration tapes, lead sheets, arrangements, and all other miscellaneous, fees and expenses incurred in behalf of said composition.</p>
                </div>
                <div class="duty-item">
                  <span class="duty-number">6.</span>
                  <p>Publisher shall be free to make licensing and sub-publication agreements in benefit of the Artist without consulting Artist and upon whatever terms it deems wise or wishes.</p>
                </div>
                <div class="duty-item">
                  <span class="duty-number">7.</span>
                  <p>The Publisher will NOT be responsible for ANY material uploaded by the Artist. The Publisher will use available technology to scan for potential fraud, copyrighted material or similarities on the beat with other published songs.</p>
                </div>
                <div class="duty-item">
                  <span class="duty-number">8.</span>
                  <p>Publisher reserves the right to amend, discontinue or terminate our Services under this Agreement, at any time and without notification to the artist. Publisher reserves the right to reject or remove any Recordings or Materials from the Stores and our Services, that you have uploaded through the Services. We also reserve the right to terminate your access to the Stores or Services without notice.</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Section 7: Legal Actions -->
          <div
            id="section-7"
            class="terms-section"
          >
            <h2 class="section-title">
              7. Legal Actions
            </h2>
            <div class="terms-content-text">
              <div class="legal-warning">
                <i class="fas fa-exclamation-triangle" />
                <p><strong>1.</strong> Copyright claims issued by third parties, stores or personas will be subject to a full investigation and if is requested by the government of any country we will help them to take legal action against the Artist and/or the Legal Name individual.</p>
              </div>

              <p><strong>2.</strong> Following actions will be immediately applied to the Artist after Copyright claim is received and investigated:</p>

              <div class="action-list">
                <div class="action-item">
                  <span class="action-letter">a)</span>
                  <p>If Artist is found culpable of his/her actions, Immediate takedown of the Artist's content will be applied without notice, Artist may be contacted after the action taken via email.</p>
                </div>
                <div class="action-item">
                  <span class="action-letter">b)</span>
                  <p>If Artist generated royalties with content not being of his own property (Copyrighted content) use bots to get plays, or any other suspicious activity. Artist will NOT be entitled to reported or receive any generated royalties and his music will be takedown from our platform.</p>
                </div>
                <div class="action-item">
                  <span class="action-letter">c)</span>
                  <p>Any misuse that is made of our brand, logo and social networks will have a legal consequence to whom it is responsible, as well as the actions that derive from this will be the total responsibility of the person who acts in bad faith when making use of our brand.</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Section 8: Stores -->
          <div
            id="section-8"
            class="terms-section"
          >
            <h2 class="section-title">
              8. Stores
            </h2>
            <div class="terms-content-text">
              <p>We have entered into agreements with different Stores for the purpose of distributing your Recordings. You may choose the Stores you want your Recording to be delivered to (PRO accounts). The agreements that Indiefy enter with the Stores shall determine the terms on which your Recordings are sold or made available. It might vary from time to time, which Stores Indiefy has entered into an agreement with. Therefore, Indiefy cannot guarantee the sale and/or exploitation of the Recordings in any particular Store. Indiefy assumes no liability if your Recordings are not made available in a particular Store.</p>
              <p>Indiefy assumes no liability for any interruptions, delays, errors or any suspensions of access, reports or payouts, in the Stores, neither in whole nor in part.</p>
              <p>When you upload your Recordings and Metadata through the Service, we will send your Recording to the Stores. You can set a date for the release of your Recording. However, each Store determines the delivery time for the distribution of your Recordings. We do not assume liability for the delivery times of the Stores.</p>
            </div>
          </div>

          <!-- Section 9: Our Intellectual Property -->
          <div
            id="section-9"
            class="terms-section"
          >
            <h2 class="section-title">
              9. Our Intellectual Property
            </h2>
            <div class="terms-content-text">
              <p>Indiefy Service, including but not limited to, all related technology, data, tools, and design is the property of Indiefy and its subsidiaries or its licensors. We grant you a limited, non-exclusive, revocable licence to make use of the Indiefy Service.</p>
              <p>The Indiefy trademarks, service marks, trade names, logos, domain names, and any other features of the Indiefy brand are the sole property of Indiefy and its subsidiaries. The Agreements do not grant you any rights to use any Brand Features whether for commercial or non-commercial use.</p>
              <p>We value hearing from our users, and are always interested in learning about ways we can improve the Service. If you choose to submit comments, ideas or feedback ("Feedback"), you agree that we are free to use them without any restriction or compensation to you.</p>
            </div>
          </div>

          <!-- Section 10: Service Limitations -->
          <div
            id="section-10"
            class="terms-section"
          >
            <h2 class="section-title">
              10. Service Limitations and Modifications
            </h2>
            <div class="terms-content-text">
              <p>Indiefy will make reasonable efforts to keep the Indiefy Service operational. However, certain technical difficulties or maintenance may, from time to time, result in temporary interruptions. Indiefy reserves the right, periodically and at any time, to modify or discontinue, temporarily or permanently, functions and features of the Indiefy Service, with or without notice, all without liability to you, except where prohibited by law, for any interruption, modification, or discontinuation of the Indiefy Service or any function or feature thereof.</p>
            </div>
          </div>

          <!-- Section 11: Term and Termination -->
          <div
            id="section-11"
            class="terms-section"
          >
            <h2 class="section-title">
              11. Term and Termination
            </h2>
            <div class="terms-content-text">
              <p>The terms of this Agreement shall commence and continue (the "Term") unless terminated by either Party under the Agreements.</p>

              <div class="termination-types">
                <div class="termination-item">
                  <h4>Indiefy FREE:</h4>
                  <p>You may at any time request the takedown of your music from Indiefy. If it has been online longer than 6 months after publishing, the status in our platform will change immediately. If the music has been published for less than 6 months, you either have to wait for the remaining days of this period.</p>
                </div>
                <div class="termination-item">
                  <h4>Indiefy PRO:</h4>
                  <p>You may at any time terminate and cancel Indiefy PRO services by going to your account settings, select "Subscription" and click it the Cancel Subscription button. Hereby you agree to terminate the PRO subscription as of the end of your subscription period (end of the purchased month on a monthly payment plan OR end of the purchased year on an annual payment plan). It is not possible to request a refund for the remaining days (monthly plan) or months (annual plan).</p>
                </div>
              </div>

              <p>Following from taking down all your published music, it is at any time possible to delete your artist profile on our site, and delete your user from account setting in order to not receive any mail from us anymore.</p>
              <p>Indiefy may terminate the Agreements or suspend your access to the Indiefy Service at any time for any and no reason, including in the event of your actual or suspected unauthorised use of the Indiefy Service and/or any content, or non-compliance with the Agreements. You may terminate your Indiefy Account at any time by submitting a termination request to Customer Support.</p>
              <p>Any sections of the Agreements that, either explicitly or by their nature, must remain in effect even after termination of the Agreements, shall survive termination.</p>
              <p>This Agreement shall be enforced or construed according to the laws of the State of Jalisco, Mexico.</p>
              <p>In the performance of his/her obligations of this Agreement, Artist shall be deemed an independent contractor. IN WITNESS WHEREOF, we hereunto set.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Contact Section -->
    <section class="contact-section">
      <div class="container">
        <div class="contact-card">
          <h3>Questions or Concerns?</h3>
          <p>If you have any questions about these terms and conditions, please don't hesitate to contact our support team. We're here to help you understand your rights and responsibilities.</p>
          <a
            href="mailto:support@indiefy.net"
            class="contact-button"
          >
            <i class="fas fa-envelope mr-2" />
            Contact Support
          </a>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { usePageSeo } from '@/composables/usePageSeo';

usePageSeo({
  titleKey: 'terms.seo-title',
  descriptionKey: 'terms.seo-description',
});

const tableOfContents = [
  'Definitions',
  'License Terms',
  'Distribution Process and Metadata',
  'Artist Responsibilities',
  'Revenue / Payment',
  'Publisher Responsibilities & Rights',
  'Legal Actions',
  'Stores',
  'Our Intellectual Property',
  'Service Limitations and Modifications',
  'Term and Termination',
];

const scrollToSection = (sectionNumber) => {
  const section = document.getElementById(`section-${sectionNumber}`);
  if (section) {
    section.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }
};
</script>

<style lang="scss" scoped>
@import "@/styles/abstracts/colors";
@import "@/styles/abstracts/typography";
@import "@/styles/abstracts/breakpoints";

.terms-view {
  min-height: 100vh;
  background-color: var(--color-background, #fafafa);

  .body--dark & {
    background-color: var(--color-background, #0a0a0a);
  }
}

// Hero Section
.hero-section {
  background: linear-gradient(135deg, $indiefy-blue 0%, $indiefy-lavender 100%);
  padding: 5rem 1.5rem 3rem;
  color: white;
  text-align: center;

  .hero-title {
    font-size: 3.5rem;
    font-weight: 800;
    margin-bottom: 1rem;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

    @media (max-width: $tablet) {
      font-size: 2.5rem;
    }

    @media (max-width: $mobile) {
      font-size: 2rem;
    }
  }

  .hero-subtitle {
    font-size: 1.5rem;
    font-weight: 400;
    margin-bottom: 2rem;
    opacity: 0.9;

    @media (max-width: $mobile) {
      font-size: 1.2rem;
    }
  }

  .hero-badge {
    display: inline-flex;
    align-items: center;
    background: rgba(255, 255, 255, 0.2);
    padding: 0.75rem 1.5rem;
    border-radius: 50px;
    font-weight: 600;
    backdrop-filter: blur(10px);
  }
}

// Table of Contents
.toc-section {
  padding: 3rem 1.5rem;
}

.toc-card {
  background: var(--color-background, white);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  padding: 2.5rem;
  max-width: 800px;
  margin: 0 auto;

  .body--dark & {
    background: var(--color-card-background, #1e1e1e);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  }

  .toc-title {
    font-size: 1.8rem;
    font-weight: 700;
    margin-bottom: 2rem;
    text-align: center;
    color: var(--color-text, #333);
  }

  .toc-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1rem;

    @media (max-width: $mobile) {
      grid-template-columns: 1fr;
    }
  }

  .toc-item {
    display: flex;
    align-items: center;
    padding: 1rem;
    border-radius: 12px;
    background: var(--color-background-alt, #f8f9fa);
    text-decoration: none;
    color: var(--color-text, #333);
    transition: all 0.3s ease;
    border: 2px solid transparent;

    .body--dark & {
      background: var(--color-background, #2a2a2a);
    }

    &:hover {
      transform: translateY(-2px);
      border-color: $indiefy-blue;
      box-shadow: 0 4px 16px rgba($indiefy-blue, 0.2);
    }

    .toc-number {
      width: 30px;
      height: 30px;
      background: $indiefy-blue;
      color: white;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      margin-right: 1rem;
      flex-shrink: 0;
    }

    .toc-text {
      font-weight: 600;
    }
  }
}

// Terms Content
.terms-content {
  padding: 3rem 1.5rem;
}

.content-wrapper {
  max-width: 900px;
  margin: 0 auto;
}

.terms-section {
  background: var(--color-background, white);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  padding: 3rem;
  margin-bottom: 2rem;
  scroll-margin-top: 100px;

  .body--dark & {
    background: var(--color-card-background, #1e1e1e);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: $mobile) {
    padding: 2rem 1.5rem;
  }

  .section-title {
    font-size: 2rem;
    font-weight: 700;
    color: $indiefy-blue;
    margin-bottom: 2rem;
    padding-bottom: 1rem;
    border-bottom: 2px solid rgba($indiefy-blue, 0.2);
  }
}

// Definition List
.definition-list {
  .definition-item {
    margin-bottom: 2rem;
    padding: 1.5rem;
    background: var(--color-background-alt, #f8f9fa);
    border-radius: 12px;
    border-left: 4px solid $indiefy-blue;

    .body--dark & {
      background: var(--color-background, #2a2a2a);
    }

    h4 {
      font-weight: 700;
      color: $indiefy-blue;
      margin-bottom: 0.5rem;
    }

    p {
      color: var(--color-text, #666);
      line-height: 1.6;
    }
  }
}

// Status List
.status-list {
  display: grid;
  gap: 1rem;
  margin-top: 1.5rem;

  .status-item {
    padding: 1.5rem;
    border-radius: 12px;
    border-left: 4px solid;

    &.in-review {
      background: rgba($info, 0.1);
      border-left-color: $info;
    }

    &.in-process {
      background: rgba($warning, 0.1);
      border-left-color: $warning;
    }

    &.published {
      background: rgba($success, 0.1);
      border-left-color: $success;
    }

    &.reopen {
      background: rgba($indiefy-pink, 0.1);
      border-left-color: $indiefy-pink;
    }

    &.rejected, &.strike {
      background: rgba($danger, 0.1);
      border-left-color: $danger;
    }

    .status-badge {
      display: inline-block;
      padding: 0.3rem 0.8rem;
      border-radius: 20px;
      font-size: 0.8rem;
      font-weight: 700;
      text-transform: uppercase;
      margin-bottom: 0.5rem;
      color: white;
    }

    &.in-review .status-badge { background: $info; }
    &.in-process .status-badge { background: $warning; }
    &.published .status-badge { background: $success; }
    &.reopen .status-badge { background: $indiefy-pink; }
    &.rejected .status-badge, &.strike .status-badge { background: $danger; }
  }
}

// Responsibility List
.responsibility-list {
  margin: 2rem 0;

  .responsibility-item {
    display: flex;
    margin-bottom: 1.5rem;
    padding: 1rem;
    background: var(--color-background-alt, #f8f9fa);
    border-radius: 8px;

    .body--dark & {
      background: var(--color-background, #2a2a2a);
    }

    .responsibility-letter {
      background: $indiefy-blue;
      color: white;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      font-size: 0.9rem;
      margin-right: 1rem;
      flex-shrink: 0;
    }

    p {
      margin: 0;
      line-height: 1.6;
    }
  }
}

// Revenue Highlight
.revenue-highlight {
  background: linear-gradient(135deg, $success 0%, lighten($success, 10%) 100%);
  color: white;
  padding: 2rem;
  border-radius: 16px;
  margin-bottom: 2rem;
  text-align: center;

  .revenue-percentage {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 1rem;

    .percentage-number {
      font-size: 3rem;
      font-weight: 800;
      line-height: 1;
    }

    .percentage-text {
      font-size: 1.1rem;
      opacity: 0.9;
    }
  }
}

// Minimum Payout
.minimum-payout {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin: 2rem 0;

  .payout-tier {
    background: var(--color-background-alt, #f8f9fa);
    padding: 1.5rem;
    border-radius: 12px;
    text-align: center;
    border: 2px solid $indiefy-blue;

    .body--dark & {
      background: var(--color-background, #2a2a2a);
    }

    .tier-label {
      display: block;
      font-weight: 600;
      color: $indiefy-blue;
      margin-bottom: 0.5rem;
    }

    .tier-amount {
      display: block;
      font-size: 1.5rem;
      font-weight: 800;
      color: var(--color-text, #333);
    }
  }
}

// Publisher Duties
.publisher-duties {
  .duty-item {
    display: flex;
    margin-bottom: 1.5rem;
    padding: 1rem;
    background: var(--color-background-alt, #f8f9fa);
    border-radius: 8px;

    .body--dark & {
      background: var(--color-background, #2a2a2a);
    }

    .duty-number {
      background: $indiefy-lavender;
      color: white;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      font-size: 0.9rem;
      margin-right: 1rem;
      flex-shrink: 0;
    }
  }
}

// Legal Warning
.legal-warning {
  background: rgba($danger, 0.1);
  border: 2px solid $danger;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  display: flex;
  align-items: flex-start;

  i {
    color: $danger;
    font-size: 1.5rem;
    margin-right: 1rem;
    margin-top: 0.2rem;
  }

  p {
    margin: 0;
    font-weight: 600;
  }
}

// Action List
.action-list {
  .action-item {
    display: flex;
    margin-bottom: 1rem;
    padding: 1rem;
    background: var(--color-background-alt, #f8f9fa);
    border-radius: 8px;

    .body--dark & {
      background: var(--color-background, #2a2a2a);
    }

    .action-letter {
      background: $danger;
      color: white;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      font-size: 0.9rem;
      margin-right: 1rem;
      flex-shrink: 0;
    }
  }
}

// Termination Types
.termination-types {
  margin: 2rem 0;

  .termination-item {
    margin-bottom: 2rem;
    padding: 1.5rem;
    background: var(--color-background-alt, #f8f9fa);
    border-radius: 12px;
    border-left: 4px solid $indiefy-blue;

    .body--dark & {
      background: var(--color-background, #2a2a2a);
    }

    h4 {
      color: $indiefy-blue;
      font-weight: 700;
      margin-bottom: 1rem;
    }
  }
}

// Contact Section
.contact-section {
  padding: 3rem 1.5rem;
  background: var(--color-background-alt, #f8f9fa);

  .body--dark & {
    background: var(--color-background-alt, #1a1a1a);
  }
}

.contact-card {
  background: var(--color-background, white);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  padding: 3rem;
  text-align: center;
  max-width: 600px;
  margin: 0 auto;

  .body--dark & {
    background: var(--color-card-background, #1e1e1e);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  }

  h3 {
    font-size: 2rem;
    font-weight: 700;
    color: var(--color-text, #333);
    margin-bottom: 1rem;
  }

  p {
    font-size: 1.1rem;
    color: var(--color-text-light, #666);
    margin-bottom: 2rem;
    line-height: 1.6;
  }

  .contact-button {
    display: inline-flex;
    align-items: center;
    background: linear-gradient(135deg, $indiefy-blue 0%, $indiefy-lavender 100%);
    color: white;
    padding: 1rem 2rem;
    border-radius: 50px;
    text-decoration: none;
    font-weight: 600;
    transition: all 0.3s ease;
    box-shadow: 0 4px 16px rgba($indiefy-blue, 0.3);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba($indiefy-blue, 0.4);
      color: white;
    }
  }
}

// General Text Styling
.terms-content-text {
  p {
    margin-bottom: 1.5rem;
    line-height: 1.7;
    color: var(--color-text, #555);
  }

  strong {
    color: var(--color-text, #333);
  }
}

.deductions-list {
  background: var(--color-background-alt, #f8f9fa);
  padding: 2rem;
  border-radius: 12px;
  margin: 2rem 0;

  .body--dark & {
    background: var(--color-background, #2a2a2a);
  }

  h4 {
    color: $indiefy-blue;
    font-weight: 700;
    margin-bottom: 1rem;
  }

  ul {
    list-style: none;
    padding: 0;

    li {
      padding: 0.5rem 0;
      color: var(--color-text, #555);
      line-height: 1.6;
    }
  }
}

.additional-responsibilities {
  margin-top: 2rem;

  p {
    margin-bottom: 1rem;
    line-height: 1.7;
  }
}

.payment-terms {
  margin-top: 2rem;

  p {
    margin-bottom: 1.5rem;
    line-height: 1.7;
  }
}
</style>